"use client";

import React, { useState } from "react";
import Link from "next/link";
import { 
  Utensils, 
  Wine, 
  Sparkles, 
  KanbanSquare, 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  CalendarCheck,
  Award
} from "lucide-react";
import RestaurantHero from "@/components/demos/restaurant/RestaurantHero";
import TastingMenuSection from "@/components/demos/restaurant/TastingMenuSection";
import PrivateDiningSection from "@/components/demos/restaurant/PrivateDiningSection";
import TableReservationModal from "@/components/demos/restaurant/TableReservationModal";
import PrivateDiningModal from "@/components/demos/restaurant/PrivateDiningModal";

export default function RestaurantDemoPage({ currency = "INR" }: { currency?: "INR" | "USD" }) {
  const [isReservationOpen, setIsReservationOpen] = useState(false);
  const [isPrivateDiningOpen, setIsPrivateDiningOpen] = useState(false);
  const [selectedMenuId, setSelectedMenuId] = useState<string | undefined>();
  const [selectedSalonId, setSelectedSalonId] = useState<string | undefined>();

  const handleSelectMenu = (menuId: string) => {
    setSelectedMenuId(menuId);
    setIsReservationOpen(true);
  };

  const handleSelectSalon = (salonId: string) => {
    setSelectedSalonId(salonId);
    setIsPrivateDiningOpen(true);
  };

  return (
    <div className="bg-stone-950 text-stone-100 min-h-screen">
      {/* 1. Hero Section */}
      <RestaurantHero
        currency={currency}
        onOpenReservation={() => {
          setSelectedMenuId(undefined);
          setIsReservationOpen(true);
        }}
        onOpenPrivateDining={() => {
          setSelectedSalonId(undefined);
          setIsPrivateDiningOpen(true);
        }}
      />

      {/* 2. Automation Flow Explainer Banner (For prospective Johnson Softwares clients) */}
      <div className="bg-gradient-to-r from-amber-950/40 via-stone-900 to-amber-950/40 border-y border-amber-500/20 py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <div className="inline-flex items-center gap-2 text-xs font-bold text-amber-400 uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Full Hospitality Automation In Action</span>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-white">
              Every Reservation Here Instantly Synchronizes with the Restaurant Sales & Floor CRM
            </h3>
            <p className="text-xs sm:text-sm text-stone-300">
              Try reserving a table above: watch how it captures guest dietary needs, allocates tables, dispatches PDF vouchers via Hostinger SMTP, and tracks revenue.
            </p>
          </div>

          <Link
            href="/demos/restaurant/crm"
            className="shrink-0 px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs shadow-lg shadow-amber-600/20 flex items-center gap-2 transition-all hover:scale-105"
          >
            <KanbanSquare className="w-4 h-4" />
            <span>Open Restaurant Floor CRM</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* 3. Tasting Menus Section */}
      <TastingMenuSection
        currency={currency}
        onSelectMenu={handleSelectMenu}
      />

      {/* 4. Private Dining & Salons Section */}
      <PrivateDiningSection
        currency={currency}
        onSelectSalon={handleSelectSalon}
      />

      {/* 5. The L'Aura Philosophy Section */}
      <section className="py-20 bg-stone-900/40 border-b border-stone-800 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-amber-400">
                The Culinary Vision
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
                Choreographed Flavors, <br />
                Subterranean Vintages.
              </h2>
              <p className="text-stone-300 text-sm sm:text-base leading-relaxed">
                Founded on the premise that fine dining should be an immersive theatrical performance, L'Aura combines dry-aged artisanal meats, foraged seasonal produce, and a 2,400-bottle subterranean limestone cellar.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="p-4 rounded-xl bg-stone-950 border border-stone-800 space-y-1">
                  <div className="flex items-center gap-2 text-amber-400 font-bold text-sm">
                    <Wine className="w-4 h-4" />
                    <span>Grand Cru Cellar</span>
                  </div>
                  <p className="text-xs text-stone-400">Rare Bordeaux, Burgundy, and biodynamic natural wine flights.</p>
                </div>

                <div className="p-4 rounded-xl bg-stone-950 border border-stone-800 space-y-1">
                  <div className="flex items-center gap-2 text-amber-400 font-bold text-sm">
                    <Award className="w-4 h-4" />
                    <span>Michelin Brigade</span>
                  </div>
                  <p className="text-xs text-stone-400">Headed by European brigade alumni with over 18 years in gastronomy.</p>
                </div>
              </div>

              <div className="pt-4 flex items-center gap-4">
                <button
                  onClick={() => setIsReservationOpen(true)}
                  className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-bold text-xs shadow-lg transition-all"
                >
                  Reserve Your Table
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-amber-500/20 shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&w=1000&q=80"
                  alt="Wine Cellar"
                  className="w-full h-96 object-cover object-center brightness-90"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-stone-950/95 border border-amber-500/30 p-5 rounded-2xl shadow-xl hidden sm:block max-w-xs">
                <span className="text-xs font-bold text-amber-400 block mb-1">Sommelier Cellar Note</span>
                <p className="text-xs text-stone-300">
                  "Every bottle in our vault is temperature-monitored at 13°C and rested to ensure flawless balance when poured."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Footer */}
      <footer className="bg-stone-950 text-stone-400 py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
          <div className="space-y-3">
            <h4 className="text-base font-bold text-white tracking-wider uppercase">L'AURA</h4>
            <p className="text-xs text-stone-400">
              Modern Gastronomy, Tasting Degustation & Vintage Wine Vault.
            </p>
            <span className="text-[11px] text-amber-400 font-semibold block">
              Powered by Johnson Softwares Hospitality CRM Engine
            </span>
          </div>

          <div className="space-y-2 text-xs">
            <h5 className="font-bold text-white uppercase tracking-wider">Service Sittings</h5>
            <p>Lunch: 12:30 PM – 03:00 PM</p>
            <p>Dinner Sitting 1: 06:30 PM – 08:15 PM</p>
            <p>Dinner Sitting 2: 08:30 PM – 11:30 PM</p>
            <p className="text-stone-300">Closed on Mondays</p>
          </div>

          <div className="space-y-2 text-xs">
            <h5 className="font-bold text-white uppercase tracking-wider">Coordinates</h5>
            <p className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span>100ft Road, Indiranagar, Bangalore</span>
            </p>
            <p className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span>+91 98808 93416</span>
            </p>
            <p className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span>reservations@laura-dining.com</span>
            </p>
          </div>

          <div className="space-y-3">
            <h5 className="font-bold text-white uppercase tracking-wider text-xs">Want This For Your Venue?</h5>
            <p className="text-xs text-stone-400">
              We design and automate custom CRM solutions for luxury restaurants, fine dining groups, and hotel chains.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-400 hover:text-amber-300 transition-colors"
            >
              <span>Schedule Engineering Call</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        <div className="max-w-7xl mx-auto pt-6 border-t border-stone-900 text-center text-xs text-stone-300 flex flex-wrap items-center justify-between gap-4">
          <p>© 2026 L'Aura Modern Gastronomy. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link href="/demos" className="hover:text-amber-400 transition-colors">Master Demos Hub</Link>
            <span>•</span>
            <Link href="/demos/restaurant/crm" className="hover:text-amber-400 transition-colors">Floor & Reservation CRM</Link>
            <span>•</span>
            <Link href="/" className="hover:text-amber-400 transition-colors">Johnson Softwares Home</Link>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <TableReservationModal
        isOpen={isReservationOpen}
        onClose={() => setIsReservationOpen(false)}
        currency={currency}
        initialMenuId={selectedMenuId}
        onReservationCreated={() => {}}
      />

      <PrivateDiningModal
        isOpen={isPrivateDiningOpen}
        onClose={() => setIsPrivateDiningOpen(false)}
        currency={currency}
        initialSalonId={selectedSalonId}
        onInquirySubmitted={() => {}}
      />
    </div>
  );
}
